/**
 * 执行回执上报：POST /api/v2/agent/commands/result
 * 后端 Verifier 校验 expectedState 并返回 planStatus/answerPatch/nextCommands。
 */
import { reportAgentCommandResult } from "@/services/api/agentV2Api";
import { reportAgentV4CommandResult } from "@/services/api/agentV4Api";
import type {
  AgentUiCommand,
  CommandReceipt,
  CommandReceiptRequest,
  CommandResultResponse
} from "@/agent/types";

export interface AgentCommandReporter {
  report(
    context: { sessionId: string; turnId: string; planId: string },
    command: AgentUiCommand,
    receipt: CommandReceipt,
    signal?: AbortSignal
  ): Promise<CommandResultResponse>;
}

export class BackendAgentCommandReporter implements AgentCommandReporter {
  async report(
    context: { sessionId: string; turnId: string; planId: string },
    command: AgentUiCommand,
    receipt: CommandReceipt,
    signal?: AbortSignal
  ): Promise<CommandResultResponse> {
    const payload: CommandReceiptRequest = {
      sessionId: context.sessionId,
      turnId: context.turnId,
      planId: context.planId,
      commandId: command.commandId,
      status: receipt.status,
      errorCode: receipt.errorCode,
      message: receipt.message,
      observedState: receipt.observedState,
      durationMs: receipt.durationMs
    };
    try {
      return await reportAgentCommandResult(payload, signal);
    } catch {
      // 上报失败不阻断队列；按本地回执继续（后端下次轮次可自愈）
      return { planStatus: "unknown", answerPatch: "", nextCommands: [] };
    }
  }
}

/** V4 Shadow reporter; it uses the same receipt JSON but never calls V2. */
export class BackendAgentV4CommandReporter implements AgentCommandReporter {
  async report(
    context: { sessionId: string; turnId: string; planId: string },
    command: AgentUiCommand,
    receipt: CommandReceipt,
    signal?: AbortSignal
  ): Promise<CommandResultResponse> {
    const payload: CommandReceiptRequest = {
      sessionId: context.sessionId,
      turnId: context.turnId,
      planId: context.planId,
      commandId: command.commandId,
      status: receipt.status,
      errorCode: receipt.errorCode,
      message: receipt.message,
      observedState: receipt.observedState,
      durationMs: receipt.durationMs
    };
    // V4 uses the server receipt as its commit boundary.  Do not turn a
    // transport failure into an "unknown but successful" result: doing so
    // lets the local queue finish while the graph is still waiting for the
    // missing receipt, which leaves the user-facing answer stuck at
    // “页面操作正在执行中”。The executor retries this exact idempotent payload.
    return reportAgentV4CommandResult(payload, signal);
  }
}
